"use client";

import type {
  AnalyticsEnvironment,
  ClientAnalyticsEvent,
  ClientEmittedEvent,
  ExperimentContext,
} from "@health/types";
import { useCallback, useEffect, useRef } from "react";
import { useOptionalChatClient } from "../../ChatProvider";
import { useAnalyticsContext } from "./context";

/**
 * Client-side analytics hook. Batches emissions per 100ms microtask window,
 * ships via `HealthCompanionClient.track()`, and falls back to
 * `navigator.sendBeacon` on unload so critical conversion events
 * (e.g. `checkout:confirm_order_success`) don't drop when the user navigates.
 *
 * `insert_id` is generated per event (UUIDv4) to give Amplitude an
 * idempotency key across retries, StrictMode remounts, and beacon replays.
 */

const BATCH_FLUSH_MS = 100;

function randomId(): string {
  // crypto.randomUUID is available in all modern browsers + Node 19+.
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

export type TrackContext = {
  sessionId: string;
  visit_type?: string;
  site?: "hims" | "hers";
  /**
   * Deployment environment. When omitted, the server re-derives it from
   * env vars on the relay; setting it here pins the client-originated value.
   */
  environment?: AnalyticsEnvironment;
  experiment?: ExperimentContext;
};

export type TrackFn = <N extends ClientEmittedEvent>(
  event: N,
  properties?: Record<string, unknown>,
) => void;

/**
 * Overload: pass an explicit `TrackContext` OR omit it to read from the
 * surrounding `<AnalyticsProvider>`. Widgets should use the latter so they
 * don't have to thread session/experiment through props.
 */
export function useTrack(contextOverride?: TrackContext): TrackFn {
  const client = useOptionalChatClient();
  const fromProvider = useAnalyticsContext();
  const ctx: TrackContext = contextOverride ??
    (fromProvider as TrackContext | null) ?? {
      sessionId: "",
    };
  const queueRef = useRef<ClientAnalyticsEvent[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contextRef = useRef(ctx);
  contextRef.current = ctx;

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const events = queueRef.current;
    if (events.length === 0) return;
    queueRef.current = [];
    if (!client) return;
    void client.track(events);
  }, [client]);

  const beaconFlush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const events = queueRef.current;
    if (events.length === 0) return;
    queueRef.current = [];
    if (!client) return;
    try {
      if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
        const blob = new Blob([JSON.stringify({ events })], { type: "application/json" });
        navigator.sendBeacon(client.analyticsTrackUrl(), blob);
        return;
      }
    } catch {
      // fallthrough
    }
    void client.track(events);
  }, [client]);

  // Flush on tab hide / unload — critical for conversion events.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onHidden = () => {
      if (document.visibilityState === "hidden") beaconFlush();
    };
    window.addEventListener("pagehide", beaconFlush);
    document.addEventListener("visibilitychange", onHidden);
    return () => {
      window.removeEventListener("pagehide", beaconFlush);
      document.removeEventListener("visibilitychange", onHidden);
    };
  }, [beaconFlush]);

  return useCallback<TrackFn>(
    (event, properties) => {
      const current = contextRef.current;
      if (!current.sessionId) return; // no-op before a session is bootstrapped
      if (!client) return; // no-op when rendered outside a ChatProvider (mock/Storybook)
      const payload: ClientAnalyticsEvent = {
        event,
        insert_id: randomId(),
        sessionId: current.sessionId,
        visit_type: current.visit_type,
        site: current.site,
        environment: current.environment,
        experiment_key: current.experiment?.experiment_key,
        variant: current.experiment?.variant,
        timestamp: Date.now(),
        properties,
      };
      queueRef.current.push(payload);
      if (!timerRef.current) {
        timerRef.current = setTimeout(flush, BATCH_FLUSH_MS);
      }
    },
    [flush, client],
  );
}
